import React, { useCallback, useMemo, useState } from 'react';

import { Collapse, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

export type AdminDrawerGroupProps = {
  children: NonNullable<React.ReactNode>;
  label: React.ReactNode;
  icon?: React.ReactNode;
  testId?: string;
};

const checkIfHasSelectedChild = (children: React.ReactNode[]): boolean => {
  let hasSelectedChild = false;

  React.Children.forEach(children, child => {
    if (!React.isValidElement(child)) {
      return;
    }

    if (child.type === AdminDrawerGroup && checkIfHasSelectedChild(child.props.children)) {
      hasSelectedChild = true;
    } else if ('to' in child.props && window.location.pathname === child.props.to) {
      hasSelectedChild = true;
    }

    return child;
  });
  return hasSelectedChild;
};

export const AdminDrawerGroup: React.FC<AdminDrawerGroupProps> = ({
  label,
  icon,
  children,
  testId = 'admin-drawer-group'
}) => {
  const { hasSelected, updatedChildren } = useMemo(() => {
    const hasSelected = checkIfHasSelectedChild(children as React.ReactNode[]);
    const updatedChildren = React.Children.toArray(children)?.filter(child => React.isValidElement(child));

    return {
      hasSelected,
      updatedChildren
    };
  }, [ children ]);
  const { classes } = useStyles();
  const [ open, setOpen ] = useState(hasSelected);
  const Icon = open ? ExpandLess : ExpandMore;
  const handleClick = useCallback(() => {
    setOpen(o => !o);
  }, []);

  if (!updatedChildren?.length) {
    return null;
  }

  return (
    <>
      <ListItemButton classes={classes} onClick={handleClick} data-testid={testId}>
        {icon && (
          <ListItemIcon>
            {icon}
          </ListItemIcon>
        )}
        <ListItemText
          primary={label}
          slotProps={{
            primary: {
              className: classes.text
            }
          }}
        />
        <Icon className={classes.icon} />
      </ListItemButton>
      <Collapse in={open} timeout="auto">
        <List component="div" disablePadding sx={{ pl: 1 }}>
          {updatedChildren}
        </List>
      </Collapse>
    </>
  );
};

const useStyles = makeStyles({ name: 'AdminDrawerGroup' })(theme => ({
  gutters: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  icon: {
    color: theme.palette.text.secondary
  },
  text: {
    fontSize: '14px'
  }
}));
